import { CounterData, PostDetail, UserDetail } from '../../types';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  Menu,
  MenuItem,
  styled,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { POST_DETAIL_API, USER_BY_IDS_API } from '../../api/endpoint';
import React, { useEffect, useState } from 'react';
import backBtn from '../../assets/backBtn.png';
import Comment from '../../components/comment';
import PostDetailsCounters from '../../components/post-details-counter';
import UserDetails from '../../components/user-details';
import axiosInstance from '../../api/axiosConfig';
import counterInstance from '../../api/counterConfig';
import createAxiosInstance from '../../api/axiosConfig';
import Loader from '../../components/loader';
import { Navigate, useNavigate } from 'react-router-dom';
import shareBtn from '../../assets/shareBtn.png';
import copylinkIcon from '../../assets/copy-link.png';
import whatsapplogo from '../../assets/whatsapplogo.jpg';
import { openAsBlob } from 'node:fs';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    backdropFilter: 'blur(40px)'
  },
  '& .MuiDialog-paper': {
    borderRadius: theme.breakpoints.up('sm') ? '20px' : '18px',
    backgroundColor: theme.breakpoints.up('sm') ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)'
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(0)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(0)
  }
}));

interface PostDetailDialogProps {
  open: boolean;
  onClose: () => void;
  postDetail: PostDetail | null;
}

const PostDetailDialog: React.FC<PostDetailDialogProps> = ({ open, onClose, postDetail }) => {
  const [counterData, setCounterData] = useState<CounterData | null>(null);
  const [userData, setUserData] = useState<UserDetail | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const openMenu = Boolean(anchorEl);

  const fetchData = async () => {
    try {
      fetchCounters();
      fetchUserDetails();
    } catch (error) {
      console.error('Error fetching counter data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCounters = async () => {
    if (postDetail) {
      try {
        const axiosInstance = createAxiosInstance(false);
        const response = await counterInstance.get(`${POST_DETAIL_API}/${postDetail.user_id}/${postDetail.post_id}`);
        const { Item } = response.data;
        setCounterData(Item);
      } catch (error) {
        console.error('Error fetching counter data:', error);
      }
    }
  };

  const fetchUserDetails = async () => {
    if (postDetail) {
      try {
        const axiosInstance = createAxiosInstance(false);
        const response = await axiosInstance.get(`${USER_BY_IDS_API}${postDetail.user_id}`);
        if (response?.data?.data?.[0]) {
          setUserData(response?.data?.data?.[0]);
        }
      } catch (error) {
        console.error('Error fetching counter data:', error);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [postDetail]);

  if (!postDetail || !counterData) return null;

  const imageUrl =
    postDetail?.imageUrl ||
    (postDetail?.files?.length
      ? `${process.env.REACT_APP_DEFAULT_AVATAR}/${postDetail.user_id}/WORKS/IMAGES/medium/${postDetail.files[0].name}`
      : '');

  const handleBack = () => {
    navigate('/');
  };

  const handleShareToWhatsApp = () => {
    const currentURL = window.location.origin + location.pathname + location.search;
    const whatsappURL = `https://wa.me/?text=${encodeURIComponent(currentURL)}`;
    window.open(whatsappURL, '_blank');
  };

  const handleCopy = () => {
    const currentPath = window.location.origin + location.pathname + location.search;
    navigator.clipboard.writeText(currentPath).catch((err) => {
      console.error('Failed to copy: ', err);
    });
  };

  const handleShare = (event: any) => {
    console.log('handleClick');
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return loading ? (
    <Loader />
  ) : (
    <BootstrapDialog open={open} onClose={onClose} sx={{ m: 0, p: 0 }} maxWidth="md" fullWidth scroll="body">
      <DialogContent
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: isMobile ? 'auto' : 400,
          maxHeight: '80vh',
          overflow: 'hidden'
        }}>
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${imageUrl || ''})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(20px)',
            zIndex: 0
          }}></div>
        {!isMobile && (
          <Button
            id="basic-button"
            aria-controls={open ? 'basic-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={openMenu ? 'true' : undefined}
            style={{ position: 'absolute', top: isMobile ? '2%' : '5%', right: '0' }}
            onClick={handleShare}>
            <img alt={'share'} src={shareBtn}></img>
          </Button>
        )}
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleClose}
          container={document.body}
          MenuListProps={{
            'aria-labelledby': 'basic-button'
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          sx={{
            '& .MuiPaper-root': {
              zIndex: 2000,
              width: isMobile ? '100px' : '150px',
              height: isMobile ? '75px' : '100px',
              background: 'rgba(255, 255, 255, 0.8)',
              color: 'black',
              backdropFilter: 'blur(12px)'
            },
            '& .MuiMenuItem-root': {
              minHeight: isMobile ? 15 : 26,
              paddingTop: isMobile ? '2px' : '4px',
              paddingBottom: isMobile ? '2px' : '4px'
            }
          }}>
          <MenuItem onClick={handleCopy}>
            <Box display="flex" alignItems="center" gap={isMobile ? 0.5 : 1}>
              <img
                alt="copy-link"
                src={copylinkIcon}
                style={{ width: isMobile ? 18 : 24, height: isMobile ? 18 : 24 }}
              />
              <Typography style={{ fontSize: isMobile ? '12px' : '18px' }}>Copy URL</Typography>
            </Box>
          </MenuItem>

          <Divider />

          <MenuItem onClick={handleShareToWhatsApp}>
            <Box display="flex" alignItems="center" gap={isMobile ? 0.5 : 1}>
              <img
                alt="whatsapp-logo"
                src={whatsapplogo}
                style={{ width: isMobile ? 18 : 24, height: isMobile ? 18 : 24 }}
              />
              <Typography style={{ fontSize: isMobile ? '12px' : '18px' }}>Share</Typography>
            </Box>
          </MenuItem>
        </Menu>
        <Button onClick={handleBack} style={{ position: 'absolute', top: 16, left: 16, zIndex: 2, cursor: 'pointer' }}>
          <img
            src={backBtn}
            alt="backBtn"
            style={{
              width: isMobile ? 24 : 32,
              height: isMobile ? 24 : 32
            }}
          />
        </Button>

        <img
          src={imageUrl || ''}
          alt={postDetail.text}
          style={{
            height: isMobile ? 'auto' : '400px',
            maxWidth: '100%',
            width: isMobile ? '100%' : 'auto',
            borderRadius: isMobile ? 0 : '10px',
            objectFit: 'contain',
            zIndex: 1,
            position: 'relative'
          }}
        />
        <UserDetails userData={userData} isMobile={isMobile} postDetail={postDetail} counterData={counterData} />
      </DialogContent>

      {/* Counters Section */}
      <PostDetailsCounters
        postDetail={postDetail}
        counterData={counterData}
        userData={userData}
        isMobile={isMobile}
        open={open}
        openMenu={openMenu}
        handleShare={handleShare}
      />

      {/* Comments */}
      <Comment
        postId={postDetail.post_id}
        count={counterData?.comments?.N ? parseInt(counterData.comments.N, 10) : 0}
      />
    </BootstrapDialog>
  );
};

export default PostDetailDialog;
